import { useCallback, useMemo, useState } from 'react';

import { useCopy } from 'new/hooks/useCopy';
import { useFeatureValue } from 'new/hooks/useFeatureFlags';
import type { CapsuleHelmDefaultsConfig } from 'new/constants/featureFlags';
import type { GeneratedCapsuleKeys } from 'new/modules/settings/capsules/domain/capsule.types';
import { getMaskedValue } from 'new/modules/settings/capsules/utils/capsuleHelpers';
import {
  buildCapsuleHelmCommand,
  CAPSULE_HELM_COMMAND_PLACEHOLDERS,
  type StringHelmCommandField,
} from '../utils/buildCapsuleHelmCommand';

export interface UseHelmCommandPreviewProps {
  generatedKeys: GeneratedCapsuleKeys;
  dataplaneUid?: string | null;
  orgId?: number | string | null;
  capsuleName?: string | null;
}

type EditableHelmCommandField = 'diagnosticsDumpsPvcExistingClaim';

const HELM_COMMAND_COPY_TRACKING_ID = 'helmCommand';

// Secret fields shown masked (`xxxx*****yyyy`) in the preview; rendered verbatim so
// the `*` isn't shell-escaped. The copied command still escapes the real values.
const MASKED_DISPLAY_FIELDS: ReadonlySet<StringHelmCommandField> = new Set([
  'apiAccessKey',
  'credentialsDecryptKey',
  'alloyTelemetryApiKey',
]);

function getMaskedKeyForDisplay(key: string): string {
  if (/^<<ADD_[A-Z0-9_]+_HERE>>$/.test(key)) return key;
  return getMaskedValue(key);
}

const VISIBLE_FIELD_CONFIG: { key: EditableHelmCommandField; label: string }[] = [
  { key: 'diagnosticsDumpsPvcExistingClaim', label: 'PVC Claim Name' },
];

function getEditableValue(value: number | string | null | undefined, placeholder: string): string {
  if (value == null) return placeholder;

  const stringValue = String(value).trim();
  return stringValue.length > 0 ? stringValue : placeholder;
}

function getConfiguredDefault(value: string | undefined): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

export function useHelmCommandPreview(props: UseHelmCommandPreviewProps) {
  const { generatedKeys, dataplaneUid, orgId, capsuleName } = props;
  const { copy, isCopied } = useCopy();

  const helmDefaults = useFeatureValue<CapsuleHelmDefaultsConfig>('UI_CAPSULE_HELM_DEFAULTS');
  const {
    chartRepository,
    version,
    httpGatewayEndpointFqdn,
    capsuleDockerImage,
    alloyTelemetryApiKey,
  } = helmDefaults ?? {};

  const capsuleDockerImageValue = getConfiguredDefault(capsuleDockerImage) ?? '';
  const [pvcClaim, setPvcClaim] = useState(
    CAPSULE_HELM_COMMAND_PLACEHOLDERS.diagnosticsDumpsPvcExistingClaim,
  );
  const [diagnosticsDumpsEnabled, setDiagnosticsDumpsEnabled] = useState(true);

  const apiAccessKey = getEditableValue(
    generatedKeys.apiAccessKey,
    CAPSULE_HELM_COMMAND_PLACEHOLDERS.apiAccessKey,
  );
  const credentialsDecryptKey = getEditableValue(
    generatedKeys.registrationKey,
    CAPSULE_HELM_COMMAND_PLACEHOLDERS.credentialsDecryptKey,
  );
  const editableDataplaneUid = getEditableValue(
    dataplaneUid,
    CAPSULE_HELM_COMMAND_PLACEHOLDERS.dataplaneUid,
  );
  const editableOrgId = getEditableValue(orgId, CAPSULE_HELM_COMMAND_PLACEHOLDERS.orgId);
  const releaseName = getEditableValue(capsuleName, CAPSULE_HELM_COMMAND_PLACEHOLDERS.releaseName);

  const baseCommandInput = useMemo(
    () => ({
      chartRepository,
      version,
      httpGatewayEndpointFqdn,
      capsuleDockerImage: capsuleDockerImageValue,
      alloyTelemetryApiKey,
      releaseName,
      apiAccessKey,
      credentialsDecryptKey,
      dataplaneUid: editableDataplaneUid,
      orgId: editableOrgId,
      diagnosticsDumpsEnabled,
      diagnosticsDumpsPvcExistingClaim: pvcClaim,
    }),
    [
      chartRepository,
      version,
      httpGatewayEndpointFqdn,
      alloyTelemetryApiKey,
      capsuleDockerImageValue,
      releaseName,
      apiAccessKey,
      credentialsDecryptKey,
      editableDataplaneUid,
      editableOrgId,
      diagnosticsDumpsEnabled,
      pvcClaim,
    ],
  );

  const commandResult = useMemo(
    () => buildCapsuleHelmCommand(baseCommandInput),
    [baseCommandInput],
  );

  const displayCommand = useMemo(
    () =>
      buildCapsuleHelmCommand(
        {
          ...baseCommandInput,
          apiAccessKey: getMaskedKeyForDisplay(apiAccessKey),
          credentialsDecryptKey: getMaskedKeyForDisplay(credentialsDecryptKey),
          alloyTelemetryApiKey: getMaskedKeyForDisplay(alloyTelemetryApiKey ?? ''),
        },
        // Masked secrets contain `*` — render them verbatim so they aren't shell-escaped.
        { unescapedFields: MASKED_DISPLAY_FIELDS },
      ).command,
    [baseCommandInput, apiAccessKey, credentialsDecryptKey, alloyTelemetryApiKey],
  );

  const isHelmCommandCopied = isCopied(HELM_COMMAND_COPY_TRACKING_ID);
  const fields = useMemo(
    () =>
      diagnosticsDumpsEnabled
        ? VISIBLE_FIELD_CONFIG.map((field) => ({
            ...field,
            value: pvcClaim,
            onChange: setPvcClaim,
          }))
        : [],
    [pvcClaim, diagnosticsDumpsEnabled],
  );

  const onDiagnosticsDumpsEnabledChange = useCallback(
    () => setDiagnosticsDumpsEnabled((value) => !value),
    [],
  );

  const handleCopyCommand = useCallback(() => {
    void copy(commandResult.command, HELM_COMMAND_COPY_TRACKING_ID);
  }, [copy, commandResult.command]);

  return {
    fields,
    displayCommand,
    diagnosticsDumpsEnabled,
    isHelmCommandCopied,
    copyButtonLabel: isHelmCommandCopied ? 'Copied!' : 'Copy Command',
    onDiagnosticsDumpsEnabledChange,
    handleCopyCommand,
  };
}
